import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Sidebar from '../../components/Dashboard/Sidebar';
import Header from '../../components/Dashboard/Header';
import Footer from '../../components/Dashboard/Footer';
import { Copy } from 'react-bootstrap-icons';
import ArchitectureLayerGraph from "../../components/Dashboard/Charts/ArchitectureLayerGraph";
import {
  listDefinitions, listVersions,
  createVersion, publishVersion,
  getGraphmlStatus, getGraphmlData,
  setArchConfig,
} from '../../utils/canvasApi';
import GraphmlEmptyState from '../../components/Dashboard/Validation/GraphmlEmptyState';
import GraphmlErrorState from '../../components/Dashboard/Validation/GraphmlErrorState';


// ── helpers ──────────────────────────────────────────────────────────────────
const STATUS_ICON = { published: '🔒', archived: '📦', draft: '✏️' };
const STATUS_LABEL = { published: 'Published', archived: 'Archived', draft: 'Draft' };
const STATUS_STYLE = {
  published: { background: '#f0fdfa', color: '#0f766e', border: '1px solid #5eead4' },
  archived: { background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0' },
  draft: { background: '#fffbeb', color: '#92400e', border: '1px solid #fcd34d' },
};

const sortVersions = (vers) =>
  [...vers].sort((a, b) => b.versionNumber - a.versionNumber);

// ─────────────────────────────────────────────────────────────────────────────

const ArchitectureLayerCanvas = () => {
  const { definitionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeSidebarItem, setActiveSidebarItem] = useState(3);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [workflowStage, setWorkflowStage] = useState('suggest');

  const [selectedCommitId, setSelectedCommitId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [definitionName, setDefinitionName] = useState("");

  const projectName = localStorage.getItem("selectedProjectName");

  // ── GraphML availability gate ─────────────────────────────────────────────
  const [graphmlStatus, setGraphmlStatus] = useState(null);
  const [graphmlLoading, setGraphmlLoading] = useState(true);
  const [graphmlError, setGraphmlError] = useState(false);

  // ── Graph data ────────────────────────────────────────────────────────────
  const [graphData, setGraphData] = useState(null);
  const [graphDataLoading, setGraphDataLoading] = useState(false);
  const [graphDataError, setGraphDataError] = useState(null);

  const commit_id_local = localStorage.getItem("commit_id");
  const isLargeScreen = windowWidth >= 992;

  const [showCopyTooltip, setShowCopyTooltip] = useState(false);

  // ── Version state ─────────────────────────────────────────────────────────
  const [versionsLoaded, setVersionsLoaded] = useState(false);
  const [defLoading, setDefLoading] = useState(false);
  const [definitionVersions, setDefinitionVersions] = useState([]);
  const [activeVersionNumber, setActiveVersionNumber] = useState(null);
  const [activeVersionStatus, setActiveVersionStatus] = useState(null);
  const [verDropdownOpen, setVerDropdownOpen] = useState(false);
  const verDropdownRef = useRef(null);

  // ── Modal + toast ─────────────────────────────────────────────────────────
  const [modal, setModal] = useState(null);
  const [modalInput, setModalInput] = useState('');
  const [modalBusy, setModalBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const showToast = useCallback((message, variant = 'error') => {
    setToast({ message, variant });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  }, []);

  const closeModal = useCallback(() => {
    setModal(null); setModalInput(''); setModalBusy(false);
  }, []);

  const networkRef = useRef(null);

  // ── Page title ────────────────────────────────────────────────────────────
  useEffect(() => {
    document.title = definitionName
      ? `DCode | Architecture definition — ${definitionName}`
      : 'DCode | Architecture definition';
  }, [definitionName]);

  // ── Initialise projectId from navigation state or localStorage ────────────
  useEffect(() => {
    if (location.state?.selectedCommitId) setSelectedCommitId(location.state.selectedCommitId);
    if (location.state?.projectId) {
      setProjectId(location.state.projectId);
    } else {
      const lsId = localStorage.getItem('selectedProjectId');
      if (lsId) setProjectId(lsId);
    }
  }, [location.state]);

  // ── Definition name — prefer navigation state, fall back to API ───────────
  useEffect(() => {
    if (location.state?.definitionName) {
      setDefinitionName(location.state.definitionName);
      return;
    }
    if (!projectId || !definitionId) return;
    listDefinitions(projectId)
      .then(defs => {
        const def = defs.find(d => d.id === definitionId);
        if (def) setDefinitionName(def.name);
      })
      .catch(() => { });
  }, [projectId, definitionId, location.state]);

  // ── Check GraphML availability ────────────────────────────────────────────
  useEffect(() => {
    if (!projectId) return;
    setGraphmlLoading(true);
    setGraphmlError(false);
    getGraphmlStatus(projectId)
      .then(s => setGraphmlStatus(s))
      .catch(() => { setGraphmlStatus(null); setGraphmlError(true); })
      .finally(() => setGraphmlLoading(false));
  }, [projectId]);

  // ── Load graph data ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!projectId || !graphmlStatus?.hasGraphML) return;
    setGraphDataLoading(true);
    setGraphDataError(null);
    getGraphmlData(projectId)
      .then(data => setGraphData(data))
      .catch(() => setGraphDataError('Failed to load dependency graph. Please refresh.'))
      .finally(() => setGraphDataLoading(false));
  }, [projectId, graphmlStatus]);

  // ── Load versions for this definition ────────────────────────────────────
  useEffect(() => {
    if (!projectId || !definitionId) return;
    setDefLoading(true);
    listVersions(projectId, definitionId)
      .then(vers => {
        const sorted = sortVersions(vers);
        setDefinitionVersions(sorted);
        if (sorted.length > 0) {
          setActiveVersionNumber(sorted[0].versionNumber);
          setActiveVersionStatus(sorted[0].status);
        }
      })
      .catch(err => console.warn('[ArchitectureLayerCanvas] listVersions failed:', err))
      .finally(() => { setDefLoading(false); setVersionsLoaded(true); });
  }, [projectId, definitionId]);

  // ── Version loaded callback ───────────────────────────────────────────────
  const handleVersionLoaded = useCallback((vd) => {
    if (vd) {
      setActiveVersionNumber(vd.versionNumber);
      setActiveVersionStatus(vd.status);
    } else {
      setActiveVersionNumber(null);
      setActiveVersionStatus(null);
    }
  }, []);

  // ── Jump to a specific version ────────────────────────────────────────────
  const handleSelectVersion = useCallback((verNum) => {
    const ver = definitionVersions.find(v => v.versionNumber === verNum);
    if (!ver) return;
    setActiveVersionNumber(ver.versionNumber);
    setActiveVersionStatus(ver.status);
    setVerDropdownOpen(false);
  }, [definitionVersions]);

  // ── Fork / create new version ─────────────────────────────────────────────
  const handleNewVersion = useCallback(async () => {
    if (!definitionId) return;
    try {
      const ver = await createVersion(
        projectId, definitionId,
        activeVersionNumber ? { fromVersionNumber: activeVersionNumber } : {}
      );
      setActiveVersionNumber(ver.versionNumber);
      setActiveVersionStatus(ver.status);
      setDefinitionVersions(prev => [ver, ...prev]);
    } catch (err) {
      showToast(err?.message || 'Failed to create version.');
    }
  }, [projectId, definitionId, activeVersionNumber, showToast]);

  // ── Publish current version ───────────────────────────────────────────────
  const handlePublish = useCallback(() => {
    if (!definitionId || !activeVersionNumber) return;
    setModal({
      type: 'confirm',
      title: `Publish v${activeVersionNumber}?`,
      body: 'This version will become permanently read-only. Fork a new version if you need to make further changes.',
      confirmLabel: 'Publish permanently',
      confirmStyle: 'danger',
      onConfirm: async () => {
        setModalBusy(true);
        try {
          await publishVersion(projectId, definitionId, activeVersionNumber);
          setActiveVersionStatus('published');
          setDefinitionVersions(prev =>
            prev.map(v => v.versionNumber === activeVersionNumber ? { ...v, status: 'published' } : v)
          );
          // Follow-up: ask user if they want this version to be the active layer
          setModalBusy(false);
          setModal({
            type: 'set-active',
            icon: '⭐',
            title: 'Set as active component?',
            body: `"${definitionName}" v${activeVersionNumber} was published. Do you want it to become the active component definition for this project?`,
            confirmLabel: 'Yes, set as active',
            cancelLabel: 'No, keep as defined',
            onConfirm: async () => {
              setModalBusy(true);
              try {
                await setArchConfig(projectId, { activeDefinitionId: definitionId, activeVersionId: activeVersionNumber });
                closeModal();
                showToast('Active component updated.');
              } catch (err) {
                setModalBusy(false);
                showToast(err?.message || 'Failed to set active component.');
              }
            },
          });
        } catch (err) {
          setModalBusy(false);
          showToast(err?.message || 'Failed to publish.');
        }
      },
    });
  }, [projectId, definitionId, activeVersionNumber, definitionName, closeModal, showToast]);

  // ── Misc helpers ──────────────────────────────────────────────────────────
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => { setShowCopyTooltip(true); setTimeout(() => setShowCopyTooltip(false), 2000); })
      .catch(() => setShowCopyTooltip(false));
  };

  const getSidebarWidth = () => {
    if (!sidebarOpen) return '0px';
    return sidebarCollapsed ? '80px' : '280px';
  };

  // ── Resize handler ────────────────────────────────────────────────────────
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setSidebarOpen(window.innerWidth >= 992);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Version dropdown click-outside ────────────────────────────────────────
  useEffect(() => {
    if (!verDropdownOpen) return;
    const handler = (e) => {
      if (verDropdownRef.current && !verDropdownRef.current.contains(e.target))
        setVerDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [verDropdownOpen]);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const toggleUserDropdown = () => setUserDropdownOpen(!userDropdownOpen);
  const handleSidebarCollapseChange = (collapsed) => setSidebarCollapsed(collapsed);

  // ── Derived ───────────────────────────────────────────────────────────────
  const activeVerStyle = STATUS_STYLE[activeVersionStatus] ?? STATUS_STYLE.draft;

  return (
    <div className="d-flex flex-column min-vh-100">
      {/* Header */}
      <div style={{
        marginLeft: isLargeScreen ? getSidebarWidth() : '0px',
        transition: 'margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative', zIndex: 1030,
      }}>
        <Header
          sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar}
          isCollapsed={sidebarCollapsed}
          userDropdownOpen={userDropdownOpen} toggleUserDropdown={toggleUserDropdown}
          pageTitle="Architecture definition"
        />
      </div>

      {/* Sidebar */}
      <Sidebar
        sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar}
        activeItem={activeSidebarItem} onItemClick={setActiveSidebarItem}
        onCollapseChange={handleSidebarCollapseChange} initialCollapsed={true}
      />

      {/* Mobile overlay */}
      {sidebarOpen && !isLargeScreen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1040 }}
          onClick={toggleSidebar}
        />
      )}

      {/* Main */}
      <main style={{
        marginLeft: isLargeScreen ? getSidebarWidth() : '0px',
        paddingTop: '70px',
        transition: 'margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        minHeight: 'calc(100vh - 140px)',
      }}>
        <div className="container-fluid px-4">
          <div className="row mb-3 align-items-center">
            <div className="col-12">
              <h1 className="display-9 fw-bold mb-2 text-gradient mt-3">
                Architecture definition
              </h1>
              {projectId && (
                <div className="d-flex flex-column gap-1">
                  <small className="text-muted fs-6">
                    <strong>Project name: </strong><span>{projectName} </span>{' '}
                    <strong className="header-small">Project ID:</strong> {projectId}
                    <span className="header-small">
                      <strong>Commit:</strong>{' '}
                      {(selectedCommitId || commit_id_local)?.substring(0, 4) || 'N/A'}...
                      {(selectedCommitId || commit_id_local) && (
                        <button
                          className="btn btn-link btn-sm p-0 ms-1"
                          onClick={() => copyToClipboard(selectedCommitId || commit_id_local)}
                          title="Copy full commit ID"
                          style={{ verticalAlign: 'baseline', color: 'black', position: 'relative', bottom: '3px' }}
                        >
                          <Copy size={16} />
                          {showCopyTooltip && <span className="ms-2">Copied!</span>}
                        </button>
                      )}
                    </span>
                  </small>
                </div>
              )}
            </div>
          </div>

          {/* ── Canvas toolbar ── */}
          {versionsLoaded && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 0,
              background: '#fff', border: '1px solid #e2e8f0',
              borderRadius: 10, marginBottom: 18,
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              position: 'relative',
            }}>

              {/* ── LEFT: Back nav + definition name ── */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', flex: '0 0 auto', borderRadius: '10px 0 0 10px' }}>
                <button
                  onClick={() => navigate('/project/architecture-compliance')}
                  title="Back to all definitions"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    background: 'none', border: '1px solid #e2e8f0',
                    borderRadius: 7, padding: '5px 10px',
                    fontSize: 12, fontWeight: 500, color: '#475569',
                    cursor: 'pointer', whiteSpace: 'nowrap',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                >
                  ← All definitions
                </button>
                {definitionName && (
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {definitionName}
                  </span>
                )}
              </div>

              {/* ── DIVIDER ── */}
              <div style={{ width: 1, alignSelf: 'stretch', background: '#e2e8f0' }} />

              {/* ── CENTRE: Workflow stage breadcrumb ── */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 0, padding: '0 16px', flex: '0 0 auto' }}>
                {[
                  { key: 'suggest', label: 'Suggest' },
                  { key: 'arrange', label: 'Arrange & Define' },
                ].map((s, i, arr) => {
                  const stages = ['suggest', 'arrange'];
                  const current = stages.indexOf(workflowStage);
                  const isActive = i === current;
                  const isDone = i < current;
                  return (
                    <React.Fragment key={s.key}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                        background: isActive ? '#0d9488' : isDone ? '#f0fdfa' : '#f8fafc',
                        color: isActive ? '#fff' : isDone ? '#0f766e' : '#94a3b8',
                        border: isActive ? '1.5px solid #0d9488' : isDone ? '1.5px solid #5eead4' : '1.5px solid #e2e8f0',
                        whiteSpace: 'nowrap', userSelect: 'none',
                      }}>
                        {isDone && <span style={{ fontSize: 10 }}>✓</span>}
                        <span style={{ fontSize: 10, opacity: 0.65, marginRight: 1 }}>{i + 1}</span>
                        {s.label}
                      </div>
                      {i < arr.length - 1 && (
                        <div style={{ width: 12, height: 1.5, background: isDone ? '#5eead4' : '#e2e8f0', flexShrink: 0 }} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              {/* ── DIVIDER ── */}
              <div style={{ width: 1, alignSelf: 'stretch', background: '#e2e8f0' }} />

              {/* ── RIGHT: Version zone ── */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', flex: 1, borderRadius: '0 10px 10px 0' }}>

                {/* Versions loading */}
                {defLoading && (
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>Loading versions…</span>
                )}

                {/* No version exists yet */}
                {!defLoading && !activeVersionNumber && (
                  <>
                    <span style={{ fontSize: 12, color: '#b45309', display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ fontSize: 13 }}>⚠</span>
                      Changes won't be saved until you create a version
                    </span>
                    <button
                      onClick={handleNewVersion}
                      title="Create v1 — your canvas will auto-save from this point"
                      style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#0d9488', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#0f766e'}
                      onMouseLeave={e => e.currentTarget.style.background = '#0d9488'}
                    >
                      Start saving
                    </button>
                  </>
                )}

                {/* Has an active version */}
                {!defLoading && activeVersionNumber && (
                  <>
                    {/* ── Version picker dropdown ── */}
                    <div ref={verDropdownRef} style={{ position: 'relative' }}>
                      <button
                        onClick={() => setVerDropdownOpen(v => !v)}
                        title="Switch version"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '4px 10px 4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                          cursor: definitionVersions.length > 1 ? 'pointer' : 'default',
                          whiteSpace: 'nowrap', letterSpacing: '0.01em',
                          ...activeVerStyle,
                          transition: 'opacity 0.15s',
                        }}
                      >
                        {STATUS_ICON[activeVersionStatus] ?? '✏️'}
                        {' '}v{activeVersionNumber} · {STATUS_LABEL[activeVersionStatus] ?? 'Draft'}
                        {definitionVersions.length > 1 && (
                          <span style={{ fontSize: 8, opacity: 0.7, marginLeft: 2, display: 'inline-block', transition: 'transform 0.15s', transform: verDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                        )}
                      </button>

                      {/* Version history dropdown */}
                      {verDropdownOpen && definitionVersions.length > 1 && (
                        <div style={{
                          position: 'absolute', top: 'calc(100% + 6px)', left: 0,
                          background: '#fff', border: '1px solid #e2e8f0',
                          borderRadius: 12, boxShadow: '0 8px 32px rgba(15,23,42,0.12)',
                          minWidth: 220, zIndex: 500, overflow: 'hidden',
                        }}>
                          <div style={{ padding: '10px 14px 8px', borderBottom: '1px solid #f1f5f9' }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                              Version history
                            </span>
                          </div>
                          <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                            {definitionVersions.map(v => {
                              const isCurrent = v.versionNumber === activeVersionNumber;
                              const vstyle = STATUS_STYLE[v.status] ?? STATUS_STYLE.draft;
                              return (
                                <button
                                  key={v.versionNumber}
                                  onClick={() => handleSelectVersion(v.versionNumber)}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: 10,
                                    width: '100%', padding: '9px 14px',
                                    background: isCurrent ? '#f0fdfa' : 'transparent',
                                    border: 'none', cursor: 'pointer', textAlign: 'left',
                                    borderLeft: `3px solid ${isCurrent ? '#0d9488' : 'transparent'}`,
                                    transition: 'background 0.12s',
                                  }}
                                  onMouseEnter={e => { if (!isCurrent) e.currentTarget.style.background = '#f8fafc'; }}
                                  onMouseLeave={e => { if (!isCurrent) e.currentTarget.style.background = 'transparent'; }}
                                >
                                  <span style={{ fontSize: 11 }}>{STATUS_ICON[v.status] ?? '✏️'}</span>
                                  <span style={{ fontSize: 13, fontWeight: isCurrent ? 600 : 400, color: '#1e293b', flex: 1 }}>
                                    v{v.versionNumber}
                                  </span>
                                  <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 20, ...vstyle, whiteSpace: 'nowrap' }}>
                                    {STATUS_LABEL[v.status] ?? 'Draft'}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Draft: Publish */}
                    {activeVersionStatus === 'draft' && (
                      <button
                        onClick={handlePublish}
                        title="Lock this version permanently — it becomes read-only and can be forked."
                        style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#0d9488', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'background 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#0f766e'}
                        onMouseLeave={e => e.currentTarget.style.background = '#0d9488'}
                      >
                        Publish
                      </button>
                    )}

                    {/* Published: Fork */}
                    {activeVersionStatus === 'published' && (
                      <button
                        onClick={handleNewVersion}
                        title="Create an editable draft forked from this published version"
                        style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#0d9488', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'background 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#0f766e'}
                        onMouseLeave={e => e.currentTarget.style.background = '#0d9488'}
                      >
                        Fork new version
                      </button>
                    )}


                  </>
                )}
              </div>
            </div>
          )}


          {/* ── Stage 1 / Stage 2 gate ── */}
          <div className="row g-3">
            <div className="col-12">
              <div style={{ minHeight: "70vh" }}>

                {graphmlLoading && (
                  <div className="text-muted" style={{ padding: "20px" }}>
                    Checking dependency graph availability…
                  </div>
                )}

                {!graphmlLoading && graphmlError && (
                  <GraphmlErrorState onRetry={() => {
                    setGraphmlLoading(true);
                    setGraphmlError(false);
                    getGraphmlStatus(projectId)
                      .then(s => setGraphmlStatus(s))
                      .catch(() => { setGraphmlStatus(null); setGraphmlError(true); })
                      .finally(() => setGraphmlLoading(false));
                  }} />
                )}

                {!graphmlLoading && !graphmlError && !graphmlStatus?.hasGraphML && (
                  <GraphmlEmptyState />
                )}

                {!graphmlLoading && !graphmlError && graphmlStatus?.hasGraphML && (
                  <>
                    {graphDataLoading && (
                      <div className="text-muted" style={{ padding: "20px" }}>
                        Loading dependency graph…
                      </div>
                    )}

                    {!graphDataLoading && graphDataError && (
                      <div style={{ color: '#dc2626', padding: 24 }}>{graphDataError}</div>
                    )}

                    {!graphDataLoading && !graphDataError && (!projectId || !versionsLoaded || defLoading) && (
                      <div className="text-muted" style={{ padding: "20px" }}>
                        Loading architecture configuration…
                      </div>
                    )}

                    {!graphDataLoading && !graphDataError && projectId && versionsLoaded && !defLoading && (
                      <ArchitectureLayerGraph
                        key={`${definitionId}-${activeVersionNumber}`}
                        graph={graphData}
                        title="Architecture dependency graph"
                        onNetworkReady={(cy) => (networkRef.current = cy)}
                        projectId={projectId}
                        definitionId={definitionId}
                        versionNumber={activeVersionNumber}
                        versionStatus={activeVersionStatus}
                        onVersionLoaded={handleVersionLoaded}
                        onStageChange={setWorkflowStage}
                        onPublish={activeVersionStatus === 'draft' ? handlePublish : null}
                      />
                    )}
                  </>
                )}

              </div>
            </div>
          </div>

        </div>
      </main>

      <Footer sidebarOpen={sidebarOpen} />

      {/* ── Modal overlay ── */}
      {modal && (
        <div
          onClick={closeModal}
          style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 14, boxShadow: '0 20px 60px rgba(0,0,0,0.18)', padding: '28px 32px', width: 400, maxWidth: '90vw' }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>
              {(modal.icon || modal.type === 'confirm') && (
                <span style={{ marginRight: 8 }}>{modal.icon || '🔒'}</span>
              )}
              {modal.title}
            </div>

            {modal.body && (
              <p style={{ fontSize: 13, color: '#64748b', marginTop: 10, marginBottom: 0, lineHeight: 1.6 }}>
                {modal.body}
              </p>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 22, justifyContent: 'flex-end' }}>
              <button
                onClick={closeModal} disabled={modalBusy}
                style={{ padding: '8px 18px', fontSize: 13, fontWeight: 500, border: '1px solid #e2e8f0', borderRadius: 8, background: '#f8fafc', color: '#475569', cursor: 'pointer' }}
              >
                {modal.cancelLabel || 'Cancel'}
              </button>
              <button
                onClick={() => { if (!modalBusy) modal.onConfirm(modalInput); }}
                disabled={modalBusy}
                style={{ padding: '8px 18px', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 8, cursor: modalBusy ? 'not-allowed' : 'pointer', background: modal.confirmStyle === 'danger' ? '#dc2626' : '#0d9488', color: '#fff', opacity: modalBusy ? 0.7 : 1 }}
              >
                {modalBusy ? 'Please wait…' : modal.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ── */}
      {toast && (
        <div
          onClick={() => setToast(null)}
          style={{ position: 'fixed', top: 28, left: '50%', transform: 'translateX(-50%)', zIndex: 2100, display: 'flex', alignItems: 'center', gap: 10, background: toast.variant === 'error' ? '#1e293b' : '#15803d', color: '#fff', borderRadius: 10, padding: '12px 18px', fontSize: 13, fontWeight: 500, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', cursor: 'pointer', maxWidth: 340, animation: 'slideInDown 0.2s ease' }}
        >
          <span style={{ fontSize: 16 }}>{toast.variant === 'error' ? '⚠' : '✓'}</span>
          {toast.message}
          <span style={{ marginLeft: 'auto', fontSize: 16, opacity: 0.6 }}>✕</span>
        </div>
      )}
      <style>{`
        @keyframes slideInDown {
          from { transform: translateX(-50%) translateY(-20px); opacity: 0; }
          to   { transform: translateX(-50%) translateY(0);     opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default ArchitectureLayerCanvas;
